import { manageAPI, msAPI } from "./index.js";

/**
 * Get ingestion history with pagination.
 *
 * `store_id` scopes the log to one seller. Without it the marketplace endpoint
 * returns history across every store, so callers rendering a single seller's
 * page must pass it.
 *
 * @param {number} page
 * @param {number} limit
 * @param {string} [storeId] current seller's store id
 */
const CDC_BASE = "merchant/admin/external-feed";

export const getIngestionHistory = async (page = 1, limit = 10, storeId) => {
  const params = { page, limit };
  if (storeId) params.store_id = storeId;

  return (await manageAPI.get(`${CDC_BASE}/history`, {
    params,
  })).data;
};

/**
 * Get matched/unmatched items recorded for a specific ingestion job.
 * @param {string} jobId Queue job ObjectID hex
 * The endpoint no longer accepts a `status` filter — passing matched/unmatched
 * returns 400, since it would apply a whole-job filter to a single page. Callers
 * filter the returned page themselves; its `counts` are page-scoped and say so
 * via `counts_scope: "page"`.
 *
 * @param {{ page?: number, limit?: number, q?: string }} params
 */
export const getIngestionHistoryItems = async (jobId, params = {}) => {
  return (await manageAPI.get(`${CDC_BASE}/history/${jobId}/items`, {
    params,
  })).data;
};

// Create a manual upload ingestion job (CSV uploaded via dashboard)
export const notifyManualCsvUpload = async ({ key, originalFileName, numRows, mapping }) => {
  return (await manageAPI.post("external-feed/manual-upload", {
    key,
    original_file_name: originalFileName,
    num_rows: numRows,
    mapping,
  })).data;
};

// Create a manual sync job (Sync Now)
// Backend will automatically look up provider from location's api_credentials
export const createSyncNowJob = async (config = {}) => {
  return (await manageAPI.post("external-feed/jobs/sync-now", {
    config
  })).data;
};

// Get queue jobs
export const getQueueJobs = async (includeInactive = false) => {
  return (await manageAPI.get(`${CDC_BASE}/jobs`, {
    params: { include_inactive: includeInactive }
  })).data;
};

// Get a specific job by ID
export const getJobById = async (jobId) => {
  return (await manageAPI.get(`${CDC_BASE}/jobs/${jobId}`)).data;
};

// Cancel a queued or in-progress job
export const cancelJob = async (jobId) => {
  return (await manageAPI.post(`${CDC_BASE}/jobs/${jobId}/cancel`)).data;
};

// =========================
// Category and Product Mappings - Currently CloverV2 ONLY
// =========================

// Get Clover categories
export const getCloverCategories = async () => {
  return (await manageAPI.get("external-feed/categories")).data;
};

// Get category mappings
export const getCategoryMappings = async () => {
  return (await manageAPI.get("external-feed/category-mappings")).data;
};

// Create category mapping
export const createCategoryMapping = async (externalCategory, internalCategory, ignore = false) => {
  return (await manageAPI.post("external-feed/category-mappings", {
    external_category: externalCategory,
    internal_category: internalCategory,
    ignore,
  })).data;
};

// Update category mapping
export const updateCategoryMapping = async (mappingId, internalCategory, ignore) => {
  const body = {};
  if (internalCategory !== undefined) {
    body.internal_category = internalCategory;
  }
  if (ignore !== undefined) {
    body.ignore = ignore;
  }
  return (await manageAPI.put(`external-feed/category-mappings/${mappingId}`, body)).data;
};

// Delete category mapping
export const deleteCategoryMapping = async (mappingId) => {
  return (await manageAPI.delete(`external-feed/category-mappings/${mappingId}`)).data;
};
// Get POS product mappings (POS products + mapping status)
export const getProductMappings = async () => {
  return (await manageAPI.get("external-feed/product-mappings")).data;
};

// Update (create/update) a product mapping for a POS product
export const updateProductMapping = async (posId, sauceySkuId) => {
  return (await manageAPI.put(`external-feed/product-mappings/${posId}`, {
    saucey_sku_id: sauceySkuId,
  })).data;
};

// Delete a product mapping (unmap)
export const deleteProductMapping = async (posId) => {
  return (await manageAPI.delete(`external-feed/product-mappings/${posId}`)).data;
};

// Mark a POS product as ignored: persists ignored flag, deactivates in pricing, excludes from future syncs
export const ignoreProductMapping = async (posId) => {
  return (await manageAPI.patch(`external-feed/product-mappings/${posId}`, {
    ignored: true,
  })).data;
};

// Clear ignored flag so the product can be mapped again in future syncs
export const unignoreProductMapping = async (posId) => {
  return (await manageAPI.patch(`external-feed/product-mappings/${posId}`, {
    ignored: false,
  })).data;
};

// ==========================================================================
// Marketplace feed rules (inventory CDC)
//
// The marketplace pipeline replaces the legacy AWS Glue inventory jobs:
// retailers push CSVs over FTP and the CDC service diffs them in Postgres.
// A marketplace "rule" is one of two things, in two tables:
//
//   column mapping  - exactly one per store, keyed by store_id. Column *names*
//                     only, so it carries renames but no transformation logic.
//   transform steps - ordered many-per-store, keyed by their own numeric id.
//                     This is where a store's imported Glue logic actually lives.
//
// These are saucey-admin only and entirely separate from the POS/Mongo rules
// above, but both answer with the same rule envelope.
// ==========================================================================



/**
 * Get a store's complete marketplace feed configuration: its column mapping,
 * its ordered transform steps, and which path the pipeline will actually take.
 *
 * Responds 503 when CDC_POSTGRES_URL is unset on the API.
 *
 * @param {string} storeId
 * @returns {Promise<{success: boolean, store: object}>}
 */
export const getMarketplaceStoreRules = async (storeId) => {
  return (await manageAPI.get(`${CDC_BASE}/rules/${encodeURIComponent(storeId)}`)).data;
};

/**
 * List column mappings across stores. `unresolvable=true` is the mapping-health
 * inbox: the stores the pipeline cannot currently ingest.
 *
 * @param {{ store_id?: string, q?: string, source?: 'glue'|'api'|'manual', unresolvable?: boolean, page?: number, limit?: number }} params
 */
export const listMarketplaceRules = async (params = {}) => {
  return (await manageAPI.get(`${CDC_BASE}/rules`, { params })).data;
};

// Get a single store's column mapping in the rule envelope
export const getMarketplaceColumnMapping = async (storeId) => {
  return (await manageAPI.get(`${CDC_BASE}/rules/mapping/${encodeURIComponent(storeId)}`)).data;
};

/**
 * Create or replace a store's column mapping.
 *
 * This is a full replace — fields omitted are cleared — and it always lands with
 * source='manual'. `sku` and `price` are required: the CDC service treats a
 * stored row as a cache and re-runs Glue extraction (overwriting the whole row)
 * whenever either is blank, so a mapping saved without them would be silently
 * reverted on the store's next file.
 *
 * @param {string} storeId
 * @param {object} mapping sku, price, upc, title, quantity, category, last_sold, discontinued, delimiter
 */
export const saveMarketplaceColumnMapping = async (storeId, mapping) => {
  return (await manageAPI.put(
    `${CDC_BASE}/rules/mapping/${encodeURIComponent(storeId)}`,
    { column_mapping: mapping }
  )).data;
};

// Delete a column mapping, handing the store back to Glue resolution on its next file
export const deleteMarketplaceColumnMapping = async (storeId) => {
  return (await manageAPI.delete(`${CDC_BASE}/rules/mapping/${encodeURIComponent(storeId)}`)).data;
};

/**
 * Append a transform step to a store's chain. Omit step_order to append.
 * @returns {Promise<{success: boolean, rule: object, warnings: string[]}>}
 */
export const createMarketplaceTransformStep = async (storeId, step) => {
  return (await manageAPI.post(`${CDC_BASE}/rules/steps`, {
    store_id: storeId,
    transform_step: step,
  })).data;
};

/**
 * Replace a step's name, SQL, enabled flag and notes. Step order is not editable
 * here — use reorderMarketplaceTransformSteps.
 */
export const updateMarketplaceTransformStep = async (stepId, step) => {
  return (await manageAPI.put(`${CDC_BASE}/rules/steps/${stepId}`, {
    transform_step: step,
  })).data;
};

// Delete a step. Refused (400) when a later step selects from it by name.
export const deleteMarketplaceTransformStep = async (stepId) => {
  return (await manageAPI.delete(`${CDC_BASE}/rules/steps/${stepId}`)).data;
};

/**
 * Toggle one step.
 *
 * Worth knowing at the call site: disabling ANY step makes the pipeline skip the
 * store's entire chain and fall back to the lossy column-mapping path, so the
 * response carries the resulting store-level warnings rather than a bare success.
 */
export const setMarketplaceTransformStepEnabled = async (stepId, enabled) => {
  return (await manageAPI.put(`${CDC_BASE}/rules/steps/${stepId}/enabled`, { enabled })).data;
};

/**
 * Rewrite a store's step order from an ordered list of step ids.
 * @returns {Promise<{success: boolean, store: object}>} the refreshed store view
 */
export const reorderMarketplaceTransformSteps = async (storeId, stepIds) => {
  return (await manageAPI.post(`${CDC_BASE}/rules/steps/reorder`, {
    store_id: storeId,
    step_ids: stepIds,
  })).data;
};

// -------------------------------------------------------------------------
// Declared inputs (transform_sources) — multi-file feeds
//
// Zero rows is the single-file majority: the one upload is registered as the
// table `source`. When rows exist, each is registered as its own table named
// `name`, which the step SQL selects FROM, and exactly one carries is_trigger.
// -------------------------------------------------------------------------

/**
 * Declare an input for a multi-file store.
 *
 * The first input created for a store becomes the trigger whether or not the
 * request says so — a manifest with no trigger is inert.
 *
 * @param {string} storeId
 * @param {{name: string, file_name: string, shared?: boolean, is_trigger?: boolean, delimiter?: string|null, pattern?: string|null}} source
 * @returns {Promise<{success: boolean, transform_source: object, warnings: string[]}>}
 */
export const createMarketplaceSource = async (storeId, source) => {
  return (await manageAPI.post(`${CDC_BASE}/rules/sources`, {
    store_id: storeId,
    transform_source: source,
  })).data;
};

export const getMarketplaceSource = async (sourceId) => {
  return (await manageAPI.get(`${CDC_BASE}/rules/sources/${sourceId}`)).data;
};

/**
 * Replace a declared input's fields.
 *
 * `shared`, `is_trigger`, `delimiter` and `pattern` are pointers server-side, so
 * an omitted key keeps its stored value. Send `delimiter: null` to clear it back
 * to "use the store's" — an empty string would override the store's with nothing.
 *
 * Renaming an input that any step's SQL selects from is refused, enabled or not.
 */
export const updateMarketplaceSource = async (sourceId, source) => {
  return (await manageAPI.put(`${CDC_BASE}/rules/sources/${sourceId}`, {
    transform_source: source,
  })).data;
};

/**
 * Delete a declared input. Refused when a step selects from it, or when it is
 * the trigger and siblings remain. Deleting the *last* input is allowed — zero
 * inputs is the single-file state, not a stranded one.
 */
export const deleteMarketplaceSource = async (sourceId) => {
  return (await manageAPI.delete(`${CDC_BASE}/rules/sources/${sourceId}`)).data;
};

/**
 * Make one input the store's trigger, clearing the others in the same
 * transaction.
 *
 * Deliberately one call: clearing and setting separately would leave a window in
 * which the store has no trigger and silently drops every upload without even
 * creating a job row.
 */
export const setMarketplaceSourceTrigger = async (sourceId) => {
  return (await manageAPI.put(`${CDC_BASE}/rules/sources/${sourceId}/trigger`)).data;
};

// CDC datastore reachability, mapping coverage, input manifest state and job backlog
export const getMarketplaceFeedHealth = async () => {
  return (await manageAPI.get(`${CDC_BASE}/health`)).data;
};

/**
 * Run a store's transform chain over a real feed file and get the result back as
 * CSV, without touching the store's saved config.
 *
 * This one goes through **msAPI (menu-service)**, not manageAPI: the CDC pod is
 * cluster-internal and authenticates its caller with a shared secret, so
 * menu-service is what proves the end user is a saucey_admin and proxies through.
 *
 * Pass `steps` to run an edited, unsaved chain — that is the point of the
 * endpoint. Omit it to run whatever is saved, disabled steps included.
 *
 * @param {object} params
 * @param {string} params.storeId
 * @param {string} params.s3Key Key within the saucey-menu-ftp bucket, no leading slash
 * @param {Array<{order?: number, name: string, sql: string}>} [params.steps]
 * @param {'final'|'canonical'} [params.format] final = the last stage's own columns
 * @param {number} [params.limit] 0/omitted = all rows, cap 100000
 * @returns {Promise<{csv: string, sourceRows: number|null, stepRows: number[]|null, filename: string|null}>}
 */
export const runTransformTest = async ({ storeId, s3Key, steps, format, limit }) => {
  const body = { store_id: storeId, s3_key: s3Key };
  if (steps && steps.length) body.steps = steps;
  if (format) body.format = format;
  if (limit) body.limit = limit;

  const res = await msAPI.post("merchant/admin/external-feed/transform-test", body, {
    responseType: "text",
    // The success body is CSV and the error body is JSON. Keep both raw so axios
    // never tries to parse a CSV as JSON; callers decode the error themselves.
    transformResponse: [(data) => data],
    timeout: 70000, // just above menu-service's 65s, which is just above the pod's 60s
  });

  const header = (name) => res.headers?.[name] ?? null;
  const toCount = (raw) => {
    const n = parseInt(raw, 10);
    return Number.isNaN(n) ? null : n;
  };

  const stepRows = header("x-cdc-step-rows");

  return {
    csv: typeof res.data === "string" ? res.data : "",
    sourceRows: toCount(header("x-cdc-source-rows")),
    stepRows: stepRows
      ? String(stepRows)
          .split(",")
          .map((n) => toCount(n.trim()))
          .filter((n) => n !== null)
      : null,
    filename: filenameFromDisposition(header("content-disposition")),
  };
};

const filenameFromDisposition = (disposition) => {
  if (!disposition) return null;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  return match ? decodeURIComponent(match[1]) : null;
};

// Get products by SKU IDs (comma-separated)
export const getProductsBySKUs = async (skuIds) => {
  const skusParam = Array.isArray(skuIds) ? skuIds.join(",") : skuIds;
  return (await manageAPI.get("products/by-skus", {
    params: { skus: skusParam }
  })).data;
};

